import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  BarChart3, CalendarDays, Gauge, Mail, Receipt, Settings, Shield, Users,
  UsersRound, CheckSquare, MessageSquare, Zap, TrendingUp, MessageCircle,
  Lock, Megaphone, FolderOpen, FileSignature, ArrowDownToLine, Sun, PieChart,
  ArrowRightLeft, Repeat, FileText, Calculator, ChevronDown, Briefcase,
  LayoutGrid, ClipboardList, Send, FileStack, LineChart, Rocket, Wrench,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { BrandMark } from "@/components/layout/Brand";
import { NotificationBell } from "@/components/layout/NotificationBell";
import { AiAssistant } from "@/components/common/AiAssistant";
import { GlobalSearch } from "@/components/layout/GlobalSearch";

const ROUTE_PERMISSIONS = {
  "/": { module: "dashboard", action: "view" },
  "/klienci": { module: "clients", action: "view" },
  "/e-maile": { module: "email", action: "view" },
  "/zadania": { module: "tasks", action: "view" },
  "/kalendarz": { module: "calendar", action: "view" },
  "/raporty": { module: "reports", action: "view" },
  "/ustawienia": { module: "settings", action: "view" },
  "/lejek": { module: "pipeline", action: "view" },
  "/kontakt": { module: "contact_center", action: "view" },
  "/faktury": { module: "invoices", action: "view" },
  "/sms": { module: "sms", action: "view" },
  "/automatyzacja": { module: "automation", action: "view" },
  "/uprawnienia": { module: "users", action: "manage_permissions" },
  "/zespoly": { module: "teams", action: "view" },
  "/szablony-zadan": { module: "tasks", action: "create" },
  "/raport-miesieczny": { module: "reports", action: "view" },
  "/umowy": { module: "clients", action: "view" },
  "/aneksy": { module: "clients", action: "view" },
  "/rentownosc": { module: "reports", action: "view" },
  "/raporty-email": { module: "reports", action: "view" },
  "/kalkulator-wynagrodzen": { module: "tasks", action: "view" },
  "/audyt": { module: "audit", action: "view" },
  "/marketing": { module: "marketing", action: "view" },
  "/dokumenty": { module: "documents", action: "view" },
  "/podpisy": { module: "signatures", action: "view" },
  "/integracje/autenti": { module: "autenti_import", action: "view" },
  "/moj-dzien": { module: "tasks", action: "view" },
  "/seo": { module: "marketing", action: "view" },
  "/jpk": { module: "jpk_converter", action: "view" },
  "/semrush": { module: "semrush", action: "view" },
  "/analityka": { module: "analytics", action: "view" },
};

// Grouped navigation
const navGroups = [
  {
    id: "main",
    label: "G\u0142\u00f3wne",
    icon: LayoutGrid,
    items: [
      { to: "/", label: "Dashboard", icon: Gauge, testid: "nav-dashboard", permission: "dashboard" },
      { to: "/moj-dzien", label: "M\u00f3j dzie\u0144", icon: Sun, testid: "nav-my-day", permission: "tasks" },
      { to: "/kalendarz", label: "Kalendarz", icon: CalendarDays, testid: "nav-calendar", permission: "calendar" },
    ],
  },
  {
    id: "clients",
    label: "Klienci i sprzeda\u017c",
    icon: Briefcase,
    items: [
      { to: "/klienci", label: "Klienci", icon: Users, testid: "nav-clients", permission: "clients" },
      { to: "/lejek", label: "Lejek sprzeda\u017cy", icon: TrendingUp, testid: "nav-pipeline", permission: "pipeline" },
      { to: "/faktury", label: "Faktury", icon: Receipt, testid: "nav-invoices", permission: "invoices" },
    ],
  },
  {
    id: "tasks",
    label: "Zadania",
    icon: ClipboardList,
    items: [
      { to: "/zadania", label: "Zadania", icon: CheckSquare, testid: "nav-tasks", permission: "tasks" },
      { to: "/szablony-zadan", label: "Szablony zada\u0144", icon: Repeat, testid: "nav-task-templates", permission: "tasks", requiredAction: "create" },
    ],
  },
  {
    id: "communication",
    label: "Komunikacja",
    icon: Send,
    items: [
      { to: "/e-maile", label: "E-maile", icon: Mail, testid: "nav-emails", permission: "email" },
      { to: "/kontakt", label: "Centrum kontaktu", icon: MessageCircle, testid: "nav-contact-center", permission: "contact_center" },
      { to: "/sms", label: "Bramka SMS", icon: MessageSquare, testid: "nav-sms", permission: "sms" },
    ],
  },
  {
    id: "documents",
    label: "Dokumenty i umowy",
    icon: FileStack,
    items: [
      { to: "/dokumenty", label: "Dokumenty", icon: FolderOpen, testid: "nav-documents", permission: "documents" },
      { to: "/podpisy", label: "Podpisy", icon: FileSignature, testid: "nav-signatures", permission: "signatures" },
      { to: "/integracje/autenti", label: "Import Autenti", icon: ArrowDownToLine, testid: "nav-autenti", permission: "autenti_import" },
      { to: "/umowy", label: "Generowanie um\u00f3w", icon: FileSignature, testid: "nav-contracts", permission: "clients" },
      { to: "/aneksy", label: "Generowanie aneks\u00f3w", icon: FileSignature, testid: "nav-annexes", permission: "clients" },
    ],
  },
  {
    id: "reports",
    label: "Raporty i analityka",
    icon: LineChart,
    items: [
      { to: "/raporty", label: "Raporty", icon: BarChart3, testid: "nav-reports", permission: "reports" },
      { to: "/raport-miesieczny", label: "Raport miesi\u0119czny", icon: FileText, testid: "nav-monthly-report", permission: "reports" },
      { to: "/rentownosc", label: "Rentowno\u015b\u0107 klient\u00f3w", icon: TrendingUp, testid: "nav-profitability", permission: "reports" },
      { to: "/raporty-email", label: "Raporty email", icon: Mail, testid: "nav-email-reports", permission: "reports" },
      { to: "/analityka", label: "Analityka", icon: PieChart, testid: "nav-analytics", permission: "analytics" },
      { to: "/kalkulator-wynagrodzen", label: "Kalkulator wynagrodze\u0144", icon: Calculator, testid: "nav-salary-calc", permission: "tasks" },
    ],
  },
  {
    id: "marketing",
    label: "Marketing i SEO",
    icon: Rocket,
    items: [
      { to: "/marketing", label: "Marketing", icon: Megaphone, testid: "nav-marketing", permission: "marketing" },
      { to: "/seo", label: "SEO & AI", icon: TrendingUp, testid: "nav-seo", permission: "marketing" },
      { to: "/semrush", label: "Semrush", icon: BarChart3, testid: "nav-semrush", permission: "semrush" },
    ],
  },
  {
    id: "admin",
    label: "Administracja",
    icon: Wrench,
    items: [
      { to: "/automatyzacja", label: "Automatyzacja", icon: Zap, testid: "nav-automation", permission: "automation" },
      { to: "/jpk", label: "Konwerter JPK", icon: ArrowRightLeft, testid: "nav-jpk", permission: "jpk_converter" },
      { to: "/uprawnienia", label: "Uprawnienia", icon: Lock, testid: "nav-permissions", permission: "users", requiredAction: "manage_permissions" },
      { to: "/zespoly", label: "Zespo\u0142y", icon: UsersRound, testid: "nav-teams", permission: "teams" },
      { to: "/ustawienia", label: "Ustawienia", icon: Settings, testid: "nav-settings", permission: "settings" },
      { to: "/audyt", label: "Audyt/RODO", icon: Shield, testid: "nav-audit", permission: "audit" },
    ],
  },
];

function GlobalSearchResults({ query, open, onClose }) {
  const [results, setResults] = useState([]);
  const nav = useNavigate();
  const ref = useRef(null);

  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    const timer = setTimeout(() => {
      api.get("/search/global", { params: { q: query } })
        .then(r => setResults(r.data.results || []))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  if (!open || !results.length) return null;
  const typeIcons = { client: Users, task: CheckSquare, document: FolderOpen, invoice: Receipt };
  const typeLabels = { client: "Klient", task: "Zadanie", document: "Dokument", invoice: "Faktura" };

  return (
    <div ref={ref} className="absolute top-full left-0 right-0 mt-1 bg-white rounded-2xl shadow-xl border border-slate-100 z-50 max-h-80 overflow-y-auto">
      {results.map((r, i) => {
        const Icon = typeIcons[r.type] || FolderOpen;
        return (
          <button key={i} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left transition-colors first:rounded-t-2xl last:rounded-b-2xl" onClick={() => {
            let url = r.url;
            if (!url) {
              if (r.type === "client") url = `/klienci/${r.id}`;
              else if (r.type === "task") url = `/zadania/${r.id}`;
              else if (r.type === "event") url = `/kalendarz?event=${r.id}`;
              else if (r.type === "invoice") url = `/faktury/${r.id}`;
              else if (r.type === "document") url = `/dokumenty?id=${r.id}`;
              else if (r.type === "email") url = `/e-maile`;
            }
            if (url) nav(url);
            onClose();
          }}>
            <div className="h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center">
              <Icon className="h-4 w-4 text-slate-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate text-slate-800">{r.title}</p>
              <p className="text-xs text-slate-400">{typeLabels[r.type]} {r.subtitle && `· ${r.subtitle}`}</p>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function NavGroup({ group, items, isActive, chatUnreadCount, onNavClick }) {
  const [open, setOpen] = useState(isActive);
  const GroupIcon = group.icon;

  useEffect(() => {
    if (isActive) setOpen(true);
  }, [isActive]);

  if (items.length === 0) return null;

  return (
    <div className="mb-1" data-testid={`nav-group-${group.id}`}>
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          "w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-[11px] font-semibold uppercase tracking-wider transition-all duration-200",
          isActive
            ? "text-[#E07A13]"
            : "text-slate-400 hover:text-slate-600"
        )}
      >
        <GroupIcon className="h-3.5 w-3.5" />
        <span className="flex-1 text-left">{group.label}</span>
        <ChevronDown className={cn("h-3 w-3 transition-transform duration-200", open && "rotate-180")} />
      </button>

      <div className={cn(
        "overflow-hidden transition-all duration-300 ease-in-out",
        open ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
      )}>
        <div className="pl-2 space-y-0.5 pt-1 pb-2">
          {items.map((item) => {
            const Icon = item.icon;
            const showBadge = item.permission === "contact_center" && chatUnreadCount > 0;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                onClick={onNavClick}
                className={({ isActive }) =>
                  cn(
                    "group flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13px] font-medium relative transition-all duration-200",
                    isActive
                      ? "bg-gradient-to-r from-[#E07A13] to-[#f09030] text-white shadow-md shadow-orange-200/50"
                      : "text-slate-500 hover:text-slate-800 hover:bg-slate-100/80"
                  )
                }
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">{item.label}</span>
                {showBadge && (
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold bg-red-500 text-white rounded-full px-1 shadow-sm">
                    {chatUnreadCount > 99 ? "99+" : chatUnreadCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function AppShell({ role, permissions, onLogout, onQuickAction, search, setSearch, chatUnreadCount = 0 }) {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    if (search.length >= 2) setSearchOpen(true);
    else setSearchOpen(false);
  }, [search]);

  const hasPermission = useCallback((module, action = "view") => {
    if (!permissions) return false;
    const modulePerms = permissions[module] || [];
    return modulePerms.includes(action);
  }, [permissions]);

  // Filter groups based on permissions
  const filteredGroups = useMemo(() => {
    return navGroups.map(group => ({
      ...group,
      filteredItems: group.items.filter(item => {
        const action = item.requiredAction || "view";
        return hasPermission(item.permission, action);
      }),
    })).filter(g => g.filteredItems.length > 0);
  }, [hasPermission]);

  // Determine which group is active
  const activeGroupId = useMemo(() => {
    for (const group of navGroups) {
      if (group.items.some(item => {
        if (item.to === "/" && location.pathname === "/") return true;
        return item.to !== "/" && location.pathname.startsWith(item.to);
      })) return group.id;
    }
    return "main";
  }, [location.pathname]);

  const actions = useMemo(() => {
    const a = [];
    if (hasPermission("clients", "create")) a.push({ key: "client", label: "Dodaj klienta", testid: "quick-add-client" });
    if (hasPermission("tasks", "create")) a.push({ key: "task", label: "Nowe zadanie", testid: "quick-add-task" });
    if (hasPermission("invoices", "create")) a.push({ key: "invoice", label: "Wystaw faktur\u0119", testid: "quick-add-invoice" });
    return a;
  }, [hasPermission]);

  const navigate = useNavigate();
  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = e.target.tagName.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || e.target.isContentEditable) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === "n" || e.key === "N") { e.preventDefault(); if (onQuickAction && hasPermission("clients", "create")) onQuickAction("client"); }
      else if (e.key === "t" || e.key === "T") { e.preventDefault(); if (onQuickAction && hasPermission("tasks", "create")) onQuickAction("task"); }
      else if (e.key === "d" || e.key === "D") { e.preventDefault(); navigate("/moj-dzien"); }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onQuickAction, hasPermission, navigate]);

  const roleLabel = role === "ADMIN" ? "Administrator" : role === "MANAGER" ? "Mened\u017cer" : "Pracownik";

  return (
    <div data-testid="app-shell" className="min-h-screen bg-[#F8FAFC] text-slate-900">
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-30 lg:hidden" onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* Sidebar */}
      <div className={cn(
        "fixed inset-y-0 left-0 w-[270px] z-40 transform transition-all duration-300 ease-in-out",
        "bg-white border-r border-slate-200/60 shadow-[4px_0_24px_rgba(0,0,0,0.03)]",
        mobileMenuOpen ? "translate-x-0" : "-translate-x-full",
        "lg:translate-x-0"
      )}>
        <div className="flex flex-col h-full">
          {/* Brand header */}
          <div className="h-16 px-5 flex items-center justify-between border-b border-slate-100/80">
            <div className="flex items-center gap-3">
              <BrandMark className="h-9 w-auto" />
              <div>
                <div className="text-sm font-bold tracking-tight text-[#0A1C3A] font-heading" data-testid="brand-title">
                  Kurdynowski CRM
                </div>
                <div className="text-[10px] font-medium text-[#E07A13] tracking-widest uppercase" data-testid="brand-subtitle">
                  Premium workspace
                </div>
              </div>
            </div>
            <button className="lg:hidden p-2 rounded-xl hover:bg-slate-100 transition-colors" onClick={() => setMobileMenuOpen(false)} aria-label="Close menu">
              <svg className="h-5 w-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* User card */}
          <div className="px-4 py-3">
            <div className="rounded-2xl bg-gradient-to-br from-[#0A1C3A] via-[#0e2548] to-[#163a6e] p-4 text-white shadow-lg shadow-slate-900/10">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-white/15 flex items-center justify-center text-sm font-bold border border-white/20">
                  {roleLabel[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-white/60 font-medium" data-testid="sidebar-role-label">Zalogowany jako</div>
                  <div className="text-sm font-semibold truncate" data-testid="sidebar-role-value">{roleLabel}</div>
                </div>
              </div>
              <Button
                data-testid="sidebar-logout-button"
                className="mt-3 w-full h-8 text-xs bg-white/10 text-white/90 border border-white/15 hover:bg-white/20 rounded-xl transition-all"
                onClick={onLogout}
              >
                Wyloguj
              </Button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent hover:scrollbar-thumb-slate-300" data-testid="sidebar-nav">
            <div className="py-2">
              {filteredGroups.map((group) => (
                <NavGroup
                  key={group.id}
                  group={group}
                  items={group.filteredItems}
                  isActive={activeGroupId === group.id}
                  chatUnreadCount={chatUnreadCount}
                  onNavClick={() => setMobileMenuOpen(false)}
                />
              ))}
            </div>
          </nav>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-slate-100/80">
            <div className="text-[10px] text-slate-300 text-center font-medium tracking-wider">
              v2.0 &middot; 2026
            </div>
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="lg:pl-[270px]">
        {/* Top header with glass effect */}
        <header className="sticky top-0 z-40 h-16 bg-white/80 backdrop-blur-xl border-b border-slate-200/50 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
          <div className="h-full px-4 md:px-6 flex items-center gap-3">
            {/* Hamburger */}
            <button
              className="lg:hidden p-2 -ml-2 rounded-xl hover:bg-slate-100 transition-colors"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open menu"
            >
              <svg className="h-6 w-6 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            {/* Search */}
            <div className="flex-1 flex items-center gap-3">
              <div className="relative max-w-xl flex-1">
                <Input
                  data-testid="header-search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Szukaj klientow, zadania, dokumenty..."
                  className="h-10 rounded-xl bg-slate-50/80 border-slate-200/80 focus-visible:ring-2 focus-visible:ring-[#E07A13]/20 focus-visible:border-[#E07A13]/40 transition-all"
                  onFocus={() => search.length >= 2 && setSearchOpen(true)}
                />
                <GlobalSearchResults query={search} open={searchOpen} onClose={() => setSearchOpen(false)} />
              </div>
              <div className="text-[11px] text-slate-300 hidden md:block font-mono" data-testid="route-hint">
                {location.pathname}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <GlobalSearch />
              <NotificationBell />
              <AiAssistant />
              {actions.map((a) => (
                <Button
                  key={a.key}
                  data-testid={`topbar-action-${a.key}`}
                  className={cn(
                    "h-10 rounded-full px-4 text-sm font-medium hidden sm:flex shadow-sm hover:shadow transition-all duration-200",
                    a.key === "invoice"
                      ? "bg-gradient-to-r from-[#E07A13] to-[#f09030] hover:from-[#c96810] hover:to-[#e08020] text-white"
                      : "bg-[#0A1C3A] hover:bg-[#07152d] text-white"
                  )}
                  onClick={() => onQuickAction(a.key)}
                >
                  <span className="hidden md:inline">{a.label}</span>
                  <span className="md:hidden">+</span>
                </Button>
              ))}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="px-4 md:px-8 py-6 md:py-8" data-testid="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
